import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { useCurrentOrg } from '@/features/org/hooks/use-org';
import { KpiOperandField } from '@/features/projects/create-kpi/components/kpi-operand-field';
import { KpiUnitField } from '@/features/projects/create-kpi/components/kpi-unit-field';
import { useAutoSetRelativeKpiDefaults } from '@/features/projects/create-kpi/hooks/use-auto-set-relative-kpi-defaults';
import { useUpsertKpisMutation } from '@/features/projects/create-kpi/hooks/use-upsert-kpi';
import {
  KpiEditorSchemaInput,
  kpiEditorSchema,
  MathOperation,
  KpiVariant,
} from '@/features/projects/create-project/validation/kpi-editor-schema';
import {
  derivativeKpisQueryKey,
  projectKpisQueryKey,
  useProjectKpis,
} from '@/features/projects/project-details/hooks/use-project-kpis';
import { isDerivativeKpi } from '@/features/projects/shared/utils/kpi-utils';
import { usePaywall } from '@/features/subscriptions/hooks/use-paywall';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import { LabelInput } from '@/shared/components/ui/label-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from '@/shared/hooks/use-toast';
import {
  DerivativeKpi,
  KpiType,
  KpiWithMetrics,
  ProjectKpi,
  ProjectType,
} from '@/shared/types/api';
import {
  handleError,
  useErrorTranslation,
} from '@/shared/utils/error-handling';
import {
  formatNumberWithDots,
  parseFormattedNumber,
} from '@/shared/utils/format';

interface KpiDialogEditorProps {
  children?: React.ReactNode;
  projectKpi?: ProjectKpi | DerivativeKpi;
  projectType: ProjectType;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const KpiDialogEditor = ({
  children,
  projectKpi,
  isOpen: externalIsOpen,
  onOpenChange,
  projectType,
}: KpiDialogEditorProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const { t } = useTranslation('project', {
    keyPrefix: 'KpiDialogEditor',
  });

  const { projectId } = useParams();
  const { data: org } = useCurrentOrg();

  const isEditing = !!projectKpi;

  const ready = !!org?.id && !!projectId;

  // Use external isOpen if provided (controlled), otherwise use internal state (uncontrolled)
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

  const handleOpenChange = (open: boolean) => {
    if (onOpenChange) {
      onOpenChange(open);
    } else {
      setInternalIsOpen(open);
    }
  };

  const onSettled = () => {
    if (onOpenChange) {
      onOpenChange(false);
    } else {
      setInternalIsOpen(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px] md:max-w-[688px]">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="mb-8 text-2xl font-semibold">
            {isEditing ? t('edit_dialog_title') : t('dialog_title')}
          </DialogTitle>
        </DialogHeader>
        {ready && (
          <KpiFormEditor
            orgId={org.id}
            projectId={projectId}
            kpi={projectKpi}
            isEditing={isEditing}
            onSettled={onSettled}
            kpiVariant="standard"
            projectType={projectType}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface KpiFormProps {
  orgId: number;
  projectId: string;
  kpi?: ProjectKpi | DerivativeKpi;
  onSettled: () => void;
  isEditing: boolean;
  kpiVariant: KpiVariant;
  projectType: ProjectType;
}

const KpiFormEditor = ({
  orgId,
  projectId,
  kpi,
  isEditing,
  onSettled,
  kpiVariant,
  projectType,
}: KpiFormProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('project', {
    keyPrefix: 'KpiFormEditor',
  });
  const { openPaywallFromError } = usePaywall();
  const errorTranslation = useErrorTranslation();
  const kpiFormSchema = kpiEditorSchema(t);
  const isAssignedKpi = kpi?.kpi_budget_id !== undefined;

  const isKpiDerivative = kpi ? isDerivativeKpi(kpi as KpiWithMetrics) : false;

  const form = useForm({
    resolver: zodResolver(kpiFormSchema),
    defaultValues: kpi
      ? {
          variant: isKpiDerivative ? 'derivative' : 'standard',
          name: kpi.name || '',
          type: (kpi.type as KpiType) || KpiType.NOMINAL,
          unit: kpi.unit || undefined,
          max_value: kpi.max_value || undefined,
          ...(isKpiDerivative &&
            'operation' in kpi && {
              operation: kpi.operation || undefined,
              kpi_operand_1: kpi.kpi_operand_1 || undefined,
              kpi_operand_2: kpi.kpi_operand_2 || undefined,
              custom_operand_1: kpi.custom_operand_1 || undefined,
              custom_operand_2: kpi.custom_operand_2 || undefined,
            }),
        }
      : {
          variant: kpiVariant,
        },
  });

  const { mutate: upsertKpis, isPending: isSubmitting } = useUpsertKpisMutation(
    {
      project_id: Number(projectId),
      onSuccess: () => {
        toast({
          title: isEditing
            ? t('update_success_message')
            : t('create_success_message'),
        });
        if (form.getValues('variant') === 'standard') {
          queryClient.invalidateQueries({
            queryKey: projectKpisQueryKey(Number(projectId), projectType),
          });
        }
        if (form.getValues('variant') === 'derivative') {
          queryClient.invalidateQueries({
            queryKey: derivativeKpisQueryKey(Number(projectId), projectType),
          });
        }
      },
      onSettled: onSettled,
      onError: async (error) => {
        const appError = await handleError(error);
        const handled = await openPaywallFromError(appError, Number(projectId));
        if (!handled) {
          toast({
            title: errorTranslation(error),
            variant: 'destructive',
          });
        }
      },
    },
  );

  // Watch for variant changes
  const selectedVariant = form.watch('variant');
  const isDerivative = selectedVariant === 'derivative';
  const isSubproject =
    projectType === 'subproject_contractor' ||
    projectType === 'subproject_client';

  // Prevent standard KPIs for subprojects - they can only create derivative KPIs
  // When editing, respect the existing KPI's type
  useEffect(() => {
    if (!isEditing && isSubproject && selectedVariant === 'standard') {
      form.setValue('variant', 'derivative');
    }
  }, [isEditing, isSubproject, selectedVariant, form]);

  useAutoSetRelativeKpiDefaults(form);

  const handleSubmit = async (values: z.infer<typeof kpiFormSchema>) => {
    upsertKpis({
      org_id: orgId,
      project_id: Number(projectId),
      project_type: projectType,
      input: {
        ...(isEditing && { id: kpi?.id, kpi_budget_id: kpi?.kpi_budget_id }),
        ...values,
      },
      type: isEditing
        ? kpi?.project_id === Number(projectId)
          ? 'edit_kpi'
          : 'edit_assigned_kpi'
        : 'create_kpi',
    });
  };

  const handleVariantChange = (value: string) => {
    const newVariant = value as KpiVariant;
    form.setValue('variant', newVariant);

    if (newVariant === 'standard') {
      form.setValue('kpi_operand_1', undefined);
      form.setValue('kpi_operand_2', undefined);
      form.setValue('custom_operand_1', undefined);
      form.setValue('custom_operand_2', undefined);
      form.resetField('operation');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        {!isEditing && (
          <Tabs
            value={selectedVariant}
            onValueChange={handleVariantChange}
            className="w-full"
          >
            <TabsList className="w-full">
              {!isSubproject && (
                <TabsTrigger value="standard" className="flex-1">
                  {t('variant_options.standard')}
                </TabsTrigger>
              )}
              <TabsTrigger value="derivative" className="flex-1">
                {t('variant_options.derivative')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        <NameField isDisabled={isAssignedKpi} />

        <TypeField isEditing={isEditing} isDisabled={isAssignedKpi} />

        <UnitAndMaxValueField isUnitDisabled={isAssignedKpi} />

        {isDerivative && (
          <DerivativeKpiFields
            projectId={projectId}
            projectType={projectType}
          />
        )}

        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            variant={'greenGradient'}
            btnType="primary"
            className="w-40"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="mr-2">
                <Loader2 className="h-4 w-4 animate-spin" />
              </span>
            ) : isEditing ? (
              t('update_button_label')
            ) : (
              t('submit_button_label')
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

const NameField = ({ isDisabled }: { isDisabled: boolean }) => {
  const form = useFormContext<KpiEditorSchemaInput>();
  const { t } = useTranslation('project', {
    keyPrefix: 'KpiFormEditor',
  });

  return (
    <FormField
      control={form.control}
      name="name"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <LabelInput
              label={t('form_field.name.name')}
              placeholder={t('form_field.name.placeholder')}
              {...field}
              disabled={isDisabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

const TypeField = ({
  isEditing,
  isDisabled,
}: {
  isEditing: boolean;
  isDisabled: boolean;
}) => {
  const form = useFormContext<KpiEditorSchemaInput>();
  const { t } = useTranslation('project', {
    keyPrefix: 'KpiFormEditor',
  });
  const { t: tKpi } = useTranslation('kpi');

  return (
    <FormField
      control={form.control}
      name="type"
      render={({ field }) => (
        <FormItem>
          <Select
            onValueChange={field.onChange}
            defaultValue={field.value}
            disabled={isEditing || isDisabled}
          >
            <FormControl>
              <SelectTrigger isInputTrigger>
                <SelectValue
                  asLabelInput
                  hideDropdownIcon={isEditing || isDisabled}
                  label={t('form_field.type.name')}
                  placeholder={t('form_field.type.placeholder')}
                >
                  {tKpi(field.value)}
                </SelectValue>
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {Object.values(KpiType).map((type) => (
                <SelectItem key={type} value={type}>
                  {tKpi(type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

const UnitAndMaxValueField = ({
  isUnitDisabled,
}: {
  isUnitDisabled: boolean;
}) => {
  const form = useFormContext<KpiEditorSchemaInput>();
  const kpiType = form.watch('type');
  const { t } = useTranslation('project', {
    keyPrefix: 'KpiFormEditor',
  });

  return (
    <>
      <KpiUnitField
        control={form.control}
        name="unit"
        label={t('form_field.unit.name')}
        placeholder={t('form_field.unit.placeholder')}
        disabled={isUnitDisabled}
        isRelative={kpiType === KpiType.RELATIVE}
      />

      <FormField
        control={form.control}
        name="max_value"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <LabelInput
                label={t('form_field.max_value.name')}
                placeholder={t('form_field.max_value.placeholder')}
                value={
                  field.value === undefined
                    ? ''
                    : formatNumberWithDots(field.value)
                }
                onChange={(e) => {
                  const parsed = parseFormattedNumber(e.target.value);
                  field.onChange(e.target.value === '' ? undefined : parsed);
                }}
                onBlur={field.onBlur}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};

const DerivativeKpiFields = ({
  projectId,
  projectType,
}: {
  projectId: string;
  projectType: ProjectType;
}) => {
  const form = useFormContext<KpiEditorSchemaInput>();
  const { t } = useTranslation('project', {
    keyPrefix: 'KpiFormEditor',
  });
  // Fetch available KPIs for operand selection
  const { data: availableKpis } = useProjectKpis({
    projectId: Number(projectId),
    projectType,
  });

  return (
    <>
      <KpiOperandField availableKpis={availableKpis ?? []} operandNumber={1} />

      <FormField
        control={form.control}
        name="operation"
        render={({ field }) => (
          <FormItem>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger isInputTrigger>
                  <SelectValue
                    asLabelInput
                    label={t('form_field.operation.name')}
                    placeholder={t('form_field.operation.placeholder')}
                  >
                    {field.value && t(`operation_options.${field.value}`)}
                  </SelectValue>
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {Object.values(MathOperation.enum).map((operation) => (
                  <SelectItem key={operation} value={operation}>
                    {t(`operation_options.${operation}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <KpiOperandField availableKpis={availableKpis ?? []} operandNumber={2} />
    </>
  );
};
